import React from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';

import { UserCompany } from '@/store/api/userApi';

import { CompanyRole, Side } from '@/utils/enums';

import {
  containerSx,
  menuItemContainerSx,
  menuItemTextSx,
  menuPaperSx,
  readonlyContainerSx,
} from './SideSelector.styles';

interface SelectorViewProps {
  selectedSide: Side | '';
  handleSideChange: (event: SelectChangeEvent) => void;
}

const SelectorView = ({
  selectedSide,
  handleSideChange,
}: SelectorViewProps) => {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>Side</InputLabel>
      <Select
        value={selectedSide || ''}
        label="Side"
        onChange={handleSideChange}
        MenuProps={{
          slotProps: {
            paper: {
              sx: menuPaperSx,
            },
          },
        }}
      >
        <MenuItem key={Side.BUYER} value={Side.BUYER}>
          <Box sx={menuItemContainerSx}>
            <Box sx={menuItemTextSx}>Buyer</Box>
          </Box>
        </MenuItem>
        <MenuItem key={Side.SELLER} value={Side.SELLER}>
          <Box sx={menuItemContainerSx}>
            <Box sx={menuItemTextSx}>Seller</Box>
          </Box>
        </MenuItem>
      </Select>
    </FormControl>
  );
};

interface ReadonlyViewProps {
  selectedCompany: UserCompany;
}

const ReadonlyView = ({ selectedCompany }: ReadonlyViewProps) => {
  return (
    <Box sx={readonlyContainerSx}>
      Side:{' '}
      {selectedCompany.companyRole === CompanyRole.BUYER_ONLY && (
        <Chip label="Buyer" size="small" color="info" variant="outlined" />
      )}
      {selectedCompany.companyRole === CompanyRole.SELLER_ONLY && (
        <Chip label="Seller" size="small" color="primary" variant="outlined" />
      )}
    </Box>
  );
};

type SideSelectorProps = {
  selectedSide: Side | '';
  selectedCompany: UserCompany;
  onSideChange: (side: Side) => void;
};

export const SideSelector: React.FC<SideSelectorProps> = ({
  selectedSide,
  selectedCompany,
  onSideChange,
}: SideSelectorProps) => {
  const isBiDirectionalRole =
    selectedCompany?.companyRole === CompanyRole.BI_DIRECTIONAL;

  const handleSideChange = (event: SelectChangeEvent) => {
    onSideChange(event.target.value as Side);
  };

  return (
    <Box sx={containerSx}>
      {isBiDirectionalRole ? (
        <SelectorView
          selectedSide={selectedSide}
          handleSideChange={handleSideChange}
        />
      ) : (
        <ReadonlyView selectedCompany={selectedCompany} />
      )}
    </Box>
  );
};
